'use client'

import { useState, useEffect, useRef } from 'react'
import { colaboradoresService, type Colaborador } from '../../services/colaboradores.service'
import { formatCPF, maskEmail, maskTelefone } from '../../utils/formatters'
import { Plus, Check, Search, X, Loader2, Mail, Phone } from 'lucide-react'

export interface CollaboratorSearchProps {
    onSelect?: (colaborador: Colaborador) => void
    onAddCollaborator?: (colaborador: Colaborador) => void
    selectedIds?: string[]
    disabled?: boolean
    placeholder?: string
    label?: string
    autoCloseOnSelect?: boolean
    filtroStatus?: 'todos' | 'em_contratacao' | 'ativos'
}

export default function CollaboratorSearch({
    onSelect,
    onAddCollaborator,
    selectedIds = [],
    disabled = false,
    placeholder = 'Digite nome ou CPF para buscar na base da empresa...',
    label = 'Buscar Colaborador Cadastrado',
    autoCloseOnSelect = false,
    filtroStatus = 'todos',
}: CollaboratorSearchProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [options, setOptions] = useState<Colaborador[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const containerRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchTerm.trim().length >= 2) {
                searchCollaborators(searchTerm.trim())
            } else {
                setOptions([])
            }
        }, 350)

        return () => clearTimeout(timer)
    }, [searchTerm])

    // Close on click outside or escape key
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [])

    const searchCollaborators = async (term: string) => {
        try {
            setLoading(true)
            setError('')
            const response = await colaboradoresService.findAll({
                search: term,
                limit: 15,
                page: 1,
                enabled: filtroStatus === 'todos' ? 'all' : true,
            })
            setOptions(response.data || [])
            setIsOpen(true)
        } catch (err) {
            console.error('Erro ao buscar colaboradores:', err)
            setError('Não foi possível carregar os colaboradores. Tente novamente.')
            setOptions([])
        } finally {
            setLoading(false)
        }
    }

    const isAlreadySelected = (colaborador: Colaborador) => {
        const colabIdStr = String(colaborador.id)
        const cleanCpf = (colaborador.cpf || '').replace(/\D/g, '')
        return selectedIds.some((id) => {
            const cleanId = String(id).replace(/\D/g, '')
            return id === colabIdStr || (cleanCpf && cleanId === cleanCpf)
        })
    }

    const handleItemAction = (colaborador: Colaborador) => {
        if (onAddCollaborator) {
            onAddCollaborator(colaborador)
            if (autoCloseOnSelect) {
                setIsOpen(false)
            }
            // Manter o foco no input para continuar pesquisando/adicionando
            if (inputRef.current) {
                inputRef.current.focus()
            }
        } else if (onSelect) {
            onSelect(colaborador)
            setIsOpen(false)
            setSearchTerm('')
            setOptions([])
        }
    }

    const handleClearInput = () => {
        setSearchTerm('')
        setOptions([])
        setIsOpen(false)
        if (inputRef.current) {
            inputRef.current.focus()
        }
    }

    return (
        <div ref={containerRef} className="relative w-full">
            <div className="relative">
                {label && (
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                        {label}
                    </label>
                )}
                <div className="relative">
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value)
                            setIsOpen(true)
                        }}
                        onFocus={() => {
                            if (options.length > 0 || searchTerm.trim().length >= 2) {
                                setIsOpen(true)
                            }
                        }}
                        placeholder={placeholder}
                        disabled={disabled}
                        className={`
                            w-full px-4 py-3 pl-11 pr-10 border border-gray-300 rounded-xl 
                            focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 
                            bg-white text-gray-900 placeholder:text-gray-400 text-sm transition-all duration-200 shadow-xs
                            ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-75' : 'hover:border-gray-400 focus:shadow-md'}
                        `}
                    />
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                        {loading ? (
                            <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
                        ) : (
                            <Search className="w-5 h-5 text-gray-400" />
                        )}
                    </div>

                    {searchTerm && (
                        <button
                            type="button"
                            onClick={handleClearInput}
                            className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                            title="Limpar busca"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            {/* Dropdown de Resultados da Busca */}
            {isOpen && (options.length > 0 || error || (searchTerm.trim().length >= 2 && !loading)) && (
                <div className="absolute z-50 w-full mt-1.5 bg-white border border-gray-200 rounded-2xl shadow-xl max-h-72 overflow-y-auto divide-y divide-gray-100 animate-in fade-in slide-in-from-top-1 duration-150">
                    {error && (
                        <div className="p-3.5 text-xs text-red-600 bg-red-50 border-b border-red-100 flex items-center justify-between">
                            <span>{error}</span>
                            <button
                                type="button"
                                onClick={() => searchCollaborators(searchTerm)}
                                className="text-red-700 underline font-medium hover:text-red-800 ml-2"
                            >
                                Tentar novamente
                            </button>
                        </div>
                    )}

                    {(() => {
                        const filteredOptions = options.filter((colab) => {
                            if (filtroStatus === 'em_contratacao') {
                                return (
                                    Boolean(colab.is_candidato) ||
                                    colab.candidato_status === 'pendente_aprovacao' ||
                                    colab.candidato_status === 'desistencia' ||
                                    colab.candidato_status === 'reprovado' ||
                                    !colab.data_admissao ||
                                    (!colab.candidato_aprovado_em && (!colab.matricula_esocial || colab.origem === 'SISTEMA'))
                                );
                            }
                            if (filtroStatus === 'ativos') {
                                return Boolean(colab.data_admissao) && !colab.is_candidato && colab.candidato_status !== 'pendente_aprovacao';
                            }
                            return true;
                        });

                        if (filteredOptions.length === 0) {
                            return (
                                <div className="p-4 text-xs text-center text-gray-500">
                                    {filtroStatus === 'em_contratacao'
                                        ? 'Nenhum candidato em contratação encontrado para o termo pesquisado.'
                                        : 'Nenhum colaborador encontrado para o termo pesquisado.'}
                                </div>
                            );
                        }

                        return (
                            <>
                                <div className="px-4 py-2 bg-slate-50 text-[11px] font-semibold text-slate-600 flex items-center justify-between sticky top-0 z-10 border-b border-gray-100">
                                    <span>
                                        {filtroStatus === 'em_contratacao'
                                            ? `Candidatos em contratação (${filteredOptions.length})`
                                            : `Colaboradores disponíveis na base (${filteredOptions.length})`}
                                    </span>
                                    <span className="text-[10px] text-emerald-700 font-bold bg-emerald-100/60 px-2 py-0.5 rounded-full">
                                        Clique em + Incluir para adicionar
                                    </span>
                                </div>
                                <ul className="divide-y divide-gray-50">
                                    {filteredOptions.map((colaborador) => {
                                        const jaIncluso = isAlreadySelected(colaborador);
                                        const isCand =
                                            !colaborador.data_admissao ||
                                            Boolean((colaborador as unknown as { is_candidato?: boolean }).is_candidato) ||
                                            colaborador.candidato_status === 'pendente_aprovacao' ||
                                            !colaborador.candidato_aprovado_em;
                                        return (
                                            <li
                                                key={colaborador.id}
                                                className={`px-4 py-3 flex items-center justify-between gap-3 transition-colors ${
                                                    jaIncluso ? 'bg-emerald-50/30' : 'hover:bg-slate-50'
                                                }`}
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-bold text-sm text-gray-900 truncate">
                                                            {colaborador.nome}
                                                        </p>
                                                        {jaIncluso ? (
                                                            <span className="shrink-0 bg-emerald-100 text-emerald-800 text-[10px] font-extrabold px-2 py-0.5 rounded-full border border-emerald-200 flex items-center gap-1">
                                                                <Check className="w-3 h-3 text-emerald-700" /> Já na fila
                                                            </span>
                                                        ) : isCand ? (
                                                            <span className="shrink-0 bg-amber-50 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-200">
                                                                Em Contratação (Candidato)
                                                            </span>
                                                        ) : !colaborador.enabled || colaborador.data_demissao ? (
                                                            <span className="shrink-0 bg-amber-100 text-amber-900 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-300">
                                                                Demitido / Recontratação
                                                            </span>
                                                        ) : (
                                                            <span className="shrink-0 bg-blue-50 text-blue-700 text-[10px] font-medium px-1.5 py-0.5 rounded border border-blue-200">
                                                                Colaborador Ativo
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-gray-500 mt-1 font-mono">
                                                        <span>CPF: {formatCPF(colaborador.cpf)}</span>
                                                    {colaborador.email && (
                                                        <span className="font-sans text-gray-600 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded text-[10px] flex items-center gap-1" title={colaborador.email}>
                                                            <Mail className="w-2.5 h-2.5 text-emerald-600" />
                                                            <span>{maskEmail(colaborador.email)}</span>
                                                        </span>
                                                    )}
                                                    {colaborador.telefone && (
                                                        <span className="font-sans text-gray-600 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded text-[10px] flex items-center gap-1" title={colaborador.telefone}>
                                                            <Phone className="w-2.5 h-2.5 text-emerald-600" />
                                                            <span>{maskTelefone(colaborador.telefone)}</span>
                                                        </span>
                                                    )}
                                                    {colaborador.cargo?.nome && (
                                                        <span className="font-sans text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded text-[11px]">
                                                            {colaborador.cargo.nome}
                                                        </span>
                                                    )}
                                                    {colaborador.setor?.nome && (
                                                        <span className="font-sans text-gray-500 text-[11px]">
                                                            Setor: {colaborador.setor.nome}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="shrink-0 ml-2">
                                                {jaIncluso ? (
                                                    <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-xl border border-emerald-200">
                                                        <Check className="w-3.5 h-3.5" /> Na fila
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            handleItemAction(colaborador)
                                                        }}
                                                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 active:scale-95 rounded-xl shadow-xs hover:shadow transition-all"
                                                    >
                                                        <Plus className="w-3.5 h-3.5" />
                                                        <span>Incluir</span>
                                                    </button>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                                </ul>
                            </>
                        );
                    })()}
                </div>
            )}
        </div>
    )
}

